/* The small shared internals of the component kit.
 *
 * Three formatting helpers and one media-query hook. They live here rather
 * than in format.ts because they are about how the KIT draws a fact, not about
 * time itself: `26 JUL 26` is a column width decision, and format.ts should not
 * have to know that the archive card has one.
 *
 * Everything here composes format.ts. Nothing here parses a date by hand.
 */

import { useEffect, useState } from 'react';

import { formatDayDate, isoDate } from '../format';
import type { TzMode } from '../format';
import type { HostRef } from '../types';

/**
 * `26 Jul 26` — the archive card's date column.
 *
 * Composed rather than asked of Intl directly: the weekday is dropped off
 * `formatDayDate` and the two-digit year comes off `isoDate`, so both halves
 * are rendered in the same zone by the same cached formatter and a card can
 * never disagree with the schedule row above it.
 *
 * `.mono` uppercases, so the case here is irrelevant on screen and correct in
 * a copy-paste.
 */
export function shortDate(iso: string | null | undefined, tz: TzMode): string {
  const day = formatDayDate(iso, tz);
  const ymd = isoDate(iso, tz);
  if (!ymd) return day;

  const words = day.split(' ');
  // "Sun 26 Jul" -> "26 Jul". A locale that answers with fewer parts is left
  // alone rather than sliced into nonsense.
  const dayMonth = words.length >= 3 ? words.slice(1).join(' ') : day;
  return `${dayMonth} ${ymd.slice(2, 4)}`;
}

/**
 * `Flytree`, `Flytree & Sombra`, `Flytree, Sombra & Nena Vidal`.
 *
 * An ampersand for the last pair because two names joined by a comma read as a
 * list that got cut off. An empty roster answers with an empty string; the
 * caller decides whether that is worth a line.
 */
export function hostNames(hosts: readonly HostRef[] | null | undefined): string {
  if (!hosts || hosts.length === 0) return '';
  const names = hosts.map((h) => h.name).filter(Boolean);
  if (names.length === 0) return '';
  if (names.length === 1) return names[0];
  return `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]}`;
}

/**
 * The host byline for a scheduled broadcast.
 *
 * A collective credit may be written directly into a booking title while the
 * actual DJ stays attached for stream authentication and reporting. When the
 * title already contains `w/`, it owns the public credit and the UI must not
 * append the authenticated host as a second byline.
 */
export function slotHostNames(
  title: string,
  hosts: readonly HostRef[] | null | undefined,
): string {
  return /\bw\/\s*\S/i.test(title) ? '' : hostNames(hosts);
}

/**
 * `HGR-047` -> `047`, the big face on the sleeve.
 *
 * The last run of digits, so `HGR-047` and `047` both answer `047` and a
 * monogram like `LP` comes back untouched — a host sleeve passes its monogram
 * through this same door.
 */
export function catalogueDigits(value: string | null | undefined): string {
  if (!value) return '';
  const digits = value.match(/(\d+)(?!.*\d)/);
  return (digits ? digits[1] : value).toUpperCase();
}

/**
 * The visitor asked their operating system for less movement.
 *
 * Read as a live subscription, not once at mount: someone who turns it on
 * while the ticker is scrolling gets it honoured immediately, and the ticker
 * is exactly the element they turned it on for. Defaults to false in an
 * environment with no matchMedia, which is the same answer the CSS gives.
 */
export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState<boolean>(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = (event: MediaQueryListEvent) => setReduced(event.matches);
    setReduced(query.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  return reduced;
}

/**
 * True while the viewport is narrower than the breakpoint tokens.css uses for
 * the mobile frame. The nav needs this in JavaScript and not only in CSS,
 * because a disclosure that is hidden by a media query but still in the tab
 * order is a keyboard trap in a page that looks fine.
 */
export function useNarrow(maxWidth = 760): boolean {
  const [narrow, setNarrow] = useState<boolean>(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(`(max-width: ${maxWidth}px)`).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const query = window.matchMedia(`(max-width: ${maxWidth}px)`);
    const onChange = (event: MediaQueryListEvent) => setNarrow(event.matches);
    setNarrow(query.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, [maxWidth]);

  return narrow;
}
